import { useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { isRgbPaintType, VehiclePaintType } from '~/shared';
import {
	CategoryTypes,
	colorSelectItems,
} from '../components/ColorSelect/ColorSelect.model';
import { useLayoutSelector } from '../redux';

export const usePaintColors = () => {
	const vehicleIndexedColors = useObservable(
		rage.vehiclePaintShop.getVehicleIndexedColors(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const brakesColorList = useObservable(
		rage.vehiclePaintShop.getVehicleBrakesColors(),
	);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const sectionIndex = useLayoutSelector(
		(state) => state.colorPicker.sectionIndex,
	);
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const indexedColor = useLayoutSelector(
		(state) => state.colorPicker.indexedColor,
	);
	const pearlColor = useLayoutSelector((state) => state.colorPicker.pearlColor);
	const brakesColor = useLayoutSelector(
		(state) => state.colorPicker.brakesColor,
	);

	const isBrakes = colorSelectItems[activeIndex]?.type === CategoryTypes.Brakes;

	const rimsColorsExtended = useMemo(() => {
		return rimsColors.map((color) => ({
			...color,
			type: VehiclePaintType.Pearl,
		}));
	}, [rimsColors]);

	const colorList = useMemo(() => {
		if (isBrakes) {
			return brakesColorList;
		}

		const currentType = activePaintGroup?.type;
		const isPaintingWheels =
			colorSelectItems[activeIndex]?.type === CategoryTypes.PaintingWheels;

		if (isPaintingWheels) {
			return rimsColorsExtended;
		}

		if (!currentType) return [];

		const isPearl = currentType === VehiclePaintType.Pearl;

		if (isPearl) {
			return pearlColors;
		}

		if (isRgbPaintType(currentType)) {
			return [];
		}

		return vehicleIndexedColors.filter((color) => color.type === currentType);
	}, [
		isBrakes,
		brakesColorList,
		activePaintGroup,
		vehicleIndexedColors,
		pearlColors,
		sectionIndex,
		activeIndex,
		rimsColorsExtended,
	]);

	const activeColor = useMemo(() => {
		if (isBrakes) {
			return colorList.find((color) => color.index === brakesColor);
		}

		const isPaintingWheels =
			colorSelectItems[activeIndex]?.type === CategoryTypes.PaintingWheels;

		if (isPaintingWheels) {
			return colorList.find((color) => color.index === rimsColor);
		}

		const currentType = activePaintGroup?.type;

		if (
			!currentType ||
			(isRgbPaintType(currentType) && currentType !== VehiclePaintType.Pearl)
		) {
			return null;
		}

		const isPearl = currentType === VehiclePaintType.Pearl;

		const colorIndex = isPearl ? pearlColor : indexedColor;

		return colorList.find(
			(color: any) =>
				(color as any).type === currentType && color.index === colorIndex,
		);
	}, [
		isBrakes,
		activePaintGroup,
		sectionIndex,
		colorList,
		rimsColor,
		activeIndex,
		pearlColor,
		indexedColor,
		brakesColor,
	]);

	return { colorList, activeColor };
};
